import React from 'react';
import { 
  Box, 
  Typography, 
  Switch,
  FormControlLabel,
  TextField,
  Chip,
  IconButton,
  InputAdornment
} from '@mui/material';
import { 
  Add as AddIcon,
  Close as CloseIcon
} from '@mui/icons-material';

interface PolicySearchPanelProps {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  searchTerms: string[];
  onSearchTermsChange: (terms: string[]) => void;
}

const PolicySearchPanel: React.FC<PolicySearchPanelProps> = ({
  enabled,
  onToggle,
  searchTerms,
  onSearchTermsChange
}) => {
  const [newTerm, setNewTerm] = React.useState('');

  const handleAddTerm = () => {
    if (newTerm.trim() && !searchTerms.includes(newTerm.trim())) {
      onSearchTermsChange([...searchTerms, newTerm.trim()]);
      setNewTerm('');
    }
  };

  const handleRemoveTerm = (term: string) => {
    onSearchTermsChange(searchTerms.filter(t => t !== term));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTerm();
    }
  };

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e) => onToggle(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Box>
            <Typography variant="body2">Enable AI Policy Search</Typography>
            <Typography variant="caption" color="textSecondary">
              Search payer websites for relevant policy documents
            </Typography>
          </Box>
        }
        sx={{ mb: 2 }}
      />

      {enabled && (
        <>
          <Typography variant="body2" sx={{ mb: 1, color: '#64748b' }}>
            Custom Search Terms (Optional)
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
            Add specific terms to include in the policy search. Leave empty to use diagnosis and procedure codes automatically.
          </Typography>
          
          <TextField
            fullWidth
            size="small"
            placeholder="Add search term..."
            value={newTerm}
            onChange={(e) => setNewTerm(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton 
                    size="small" 
                    onClick={handleAddTerm}
                    disabled={!newTerm.trim()}
                  >
                    <AddIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />

          {searchTerms.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
              {searchTerms.map((term) => (
                <Chip
                  key={term}
                  label={term}
                  size="small"
                  onDelete={() => handleRemoveTerm(term)}
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Box>
          )}

          <Box sx={{ 
            mt: 2, 
            p: 1.5, 
            backgroundColor: '#f0f9ff', 
            borderRadius: 1,
            border: '1px solid #bae6fd'
          }}>
            <Typography variant="caption" sx={{ color: '#0369a1' }}>
              <strong>How it works:</strong> The AI will search major payer websites 
              (UnitedHealthcare, Aetna, Cigna, Humana, BCBS, CMS/Medicare) for relevant 
              medical policies and coverage guidelines based on the claim's diagnosis codes, 
              procedure codes, and any custom terms you provide.
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PolicySearchPanel;
